<template>
  <div class="cms-html-editor-toolbar">
    <div>
      <UiPicker
        :options="[
          {'key': 6, 'name': $t('HtmlEditorToolbar.Paragraph')},
          {'key': 1, 'name': $t('HtmlEditorToolbar.Heading1')},
          {'key': 2, 'name': $t('HtmlEditorToolbar.Heading2')},
          {'key': 3, 'name': $t('HtmlEditorToolbar.Heading3')},
          {'key': 4, 'name': $t('HtmlEditorToolbar.Heading4')},
          {'key': 5, 'name': $t('HtmlEditorToolbar.Heading5')}
        ]"
        optionKey="key"
        optionText="name"
        :value="optionText"
        @input="optionText = $event"
        :hideSearch=true
      />
    </div>
    <template v-for="(item, index) in items">
      <div>
        <div class="divider" v-if="item.type === 'divider'" :key="`divider${index}`" />
        <HtmlEditorTiptapToolbarItem v-else :key="index" v-bind="item" />
        <div v-if="item.title === 'HtmlEditorToolbar.TextColor' || item.title === 'HtmlEditorToolbar.BackgroundColor'" :style="'border-top: 2px solid '+(item.title === 'HtmlEditorToolbar.TextColor' ? colorSelected : backgroundSelected)+'; margin-right: 5px;'">
          <HtmlEditorTiptapToolbarColor
            class="selectColor"
            v-if="item.title === 'HtmlEditorToolbar.TextColor' ? openColor : openBackground"
            @color="updateColor(item.title, $event)"
            :selectedColor="item.title === 'HtmlEditorToolbar.TextColor' ? colorSelected : backgroundSelected"
          />
        </div>
      </div>
    </template>

    <HtmlEditorTiptapToolbarCode
      :isOpen="showDialogCode"
      :value="codeHTML"
      @close="showDialogCode = false"
      @content-updated="updateExternalContent" />

    <HtmlEditorTiptapToolbarPreview
      :isOpen="showDialogPreview"
      :value="codeHTML"
      @close="showDialogPreview = false" />

    <HtmlEditorTiptapToolbarUpload
      :isLinkImage="isLinkImage"
      :isLink="isLink"
      :urlDefault="urlDefault"
      :isOpen="showDialogUpload"
      @close="showDialogUpload = false"
      @setUrl="setUrl($event)" />

    <HtmlEditorTiptapToolbarInsertTemplate
      :isOpen="showDialogTemplate"
      @close="showDialogTemplate = false"
      @contentTemplate="insertContent($event)" />
  </div>
</template>

<script>
import useI18n from '@/modules/i18n/mixins/useI18n.js';

import { UiPicker } from '@/modules/ui/components';
import HtmlEditorTiptapToolbarItem from './HtmlEditorTiptapToolbarItem.vue';
import HtmlEditorTiptapToolbarCode from './HtmlEditorTiptapToolbarCode.vue';
import HtmlEditorTiptapToolbarColor from './HtmlEditorTiptapToolbarColor.vue';
import HtmlEditorTiptapToolbarUpload from './HtmlEditorTiptapToolbarUpload.vue';
import HtmlEditorTiptapToolbarPreview from './HtmlEditorTiptapToolbarPreview.vue';
import HtmlEditorTiptapToolbarInsertTemplate from './HtmlEditorTiptapToolbarInsertTemplate.vue';

export default {
  name: 'cms-html-editor-toolbar',
  components: {
    UiPicker,
    HtmlEditorTiptapToolbarCode,
    HtmlEditorTiptapToolbarItem,
    HtmlEditorTiptapToolbarColor,
    HtmlEditorTiptapToolbarUpload,
    HtmlEditorTiptapToolbarPreview,
    HtmlEditorTiptapToolbarInsertTemplate
  },
  props: {
    editor: {
      type: [Object, String],
      default: () => {},
    },
  },
  mixins: [useI18n],
  data() {
    return {
      codeHTML: '',
      isLink: false,
      optionText: 1,
      urlDefault: null,
      openColor: false,
      isLinkImage: false,
      openBackground: false,
      showDialogCode: false,
      showDialogUpload: false,
      showDialogPreview: false,
      colorSelected: '#000000',
      showDialogTemplate: false,
      backgroundSelected: '#FFFFFF',
      items: [
        { type: "divider" },
        {
          icon: "mdi:format-align-left",
          title: "HtmlEditorToolbar.AlignLeft",
          action: () => this.editor.chain().focus().setTextAlign("left").run(),
          isActive: () => this.editor?.isActive({ textAlign: "left" }),
        },
        {
          icon: "mdi:format-align-center",
          title: "HtmlEditorToolbar.AlignCenter",
          action: () => this.editor.chain().focus().setTextAlign("center").run(),
          isActive: () => this.editor?.isActive({ textAlign: "center" }),
        },
        {
          icon: "mdi:format-align-right",
          title: "HtmlEditorToolbar.AlignRight",
          action: () => this.editor.chain().focus().setTextAlign("right").run(),
          isActive: () => this.editor?.isActive({ textAlign: "right" }),
        },
        {
          icon: "mdi:format-align-justify",
          title: "HtmlEditorToolbar.Justify",
          action: () => this.editor.chain().focus().setTextAlign("justify").run(),
          isActive: () => this.editor?.isActive({ textAlign: "justify" }),
        },
        {
          icon: "mdi:format-color-text",
          title: "HtmlEditorToolbar.TextColor",
          action: () => {
            this.editor.getAttributes('textStyle').color ? this.colorSelected = this.editor.getAttributes('textStyle').color : this.colorSelected = "#000000";
            this.openColor = !this.openColor
          },
        },
        {
          icon: "mdi:format-color-highlight",
          title: "HtmlEditorToolbar.BackgroundColor",
          action: () => {
            this.editor.getAttributes('highlight').color ? this.backgroundSelected = this.editor.getAttributes('highlight').color : this.backgroundSelected = "#FFFFFF";
            this.openBackground = !this.openBackground
          },
        },
        { type: "divider" },
        {
          icon: "mdi:format-bold",
          title: "HtmlEditorToolbar.Bold",
          action: () => this.editor.chain().focus().toggleBold().run(),
          isActive: () => this.editor?.isActive("bold"),
        },
        {
          icon: "mdi:format-italic",
          title: "HtmlEditorToolbar.Italic",
          action: () => this.editor.chain().focus().toggleItalic().run(),
          isActive: () => this.editor?.isActive("italic"),
        },
        {
          icon: "mdi:format-strikethrough",
          title: "HtmlEditorToolbar.Highlight",
          action: () => this.editor.chain().focus().toggleStrike().run(),
          isActive: () => this.editor?.isActive("strike"),
        },
        {
          icon: "mdi:format-underline",
          title: "HtmlEditorToolbar.Underline",
          action: () => this.editor.chain().focus().toggleUnderline().run(),
          isActive: () => this.editor?.isActive("underline"),
        },
        { type: "divider" },
        {
          icon: "mdi:format-list-bulleted",
          title: "HtmlEditorToolbar.BulletList",
          action: () => this.editor.chain().focus().toggleBulletList().run(),
          isActive: () => this.editor?.isActive("bulletList"),
        },
        {
          icon: "mdi:format-list-numbered",
          title: "HtmlEditorToolbar.NumberedList",
          action: () => this.editor.chain().focus().toggleOrderedList().run(),
          isActive: () => this.editor?.isActive("orderedList"),
        },
        {
          icon: "mdi:code-not-equal-variant",
          title: "HtmlEditorToolbar.CodeBlock",
          action: () => this.editor.chain().focus().toggleCodeBlock().run(),
          isActive: () => this.editor?.isActive("codeBlock"),
        },
        { type: "divider" },
        {
          icon: "mdi:link",
          title: "HtmlEditorToolbar.InsertEditLink",
          action: () => {
            const selection = this.editor.state.selection;
            const textSelected = selection && selection.$from !== selection.$to ? selection.content().content.textBetween(0, selection.content().content.size, "\n") : '';

            const isImageSelected = this.editor.getAttributes("image");
            this.isLinkImage = isImageSelected.src ? true : false;

            const previousUrl = this.editor.getAttributes("link");
            this.urlDefault = {src: previousUrl.href, text: textSelected, target: previousUrl.target};

            this.showDialogUpload = true;
            this.isLink = true;
          },
          isActive: () => this.editor?.isActive("link"),
        },
        {
          icon: "mdi:image-outline",
          title: "HtmlEditorToolbar.InsertEditImage",
          action: () => {
            const previousImg = this.editor.getAttributes("image");
            this.urlDefault = previousImg;
            this.showDialogUpload = true;
            this.isLink = false;
          },
          isActive: () => this.editor?.isActive("image"),
        },
        {
          icon: "mdi:newspaper-variant-outline",
          title: "HtmlEditorToolbar.InsertTemplate",
          action: () => this.showDialogTemplate = true,
        },
        { type: "divider" },
        {
          icon: "mdi:format-quote-open",
          title: "HtmlEditorToolbar.SubBlock",
          action: () => this.editor.chain().focus().toggleBlockquote().run(),
          isActive: () => this.editor?.isActive("blockquote"),
        },
        {
          icon: "mdi:minus",
          title: "HtmlEditorToolbar.InsertHorizontalLine",
          action: () => this.editor.chain().focus().setHorizontalRule().run(),
        },
        { type: "divider" },
        {
          icon: "mdi:format-text-wrapping-wrap",
          title: "HtmlEditorToolbar.SkipLine",
          action: () => this.editor.chain().focus().setHardBreak().run(),
        },
        {
          icon: "mdi:format-clear",
          title: "HtmlEditorToolbar.CleanFormatting",
          action: () => this.editor.chain().focus().clearNodes().unsetAllMarks().run(),
        },
        { type: "divider" },
        {
          icon: "mdi:undo",
          title: "HtmlEditorToolbar.Undo",
          action: () => this.editor.chain().focus().undo().run(),
        },
        {
          icon: "mdi:redo",
          title: "HtmlEditorToolbar.Redo",
          action: () => this.editor.chain().focus().redo().run(),
        },
        { type: "divider" },
        {
          icon: "mdi:eye-outline",
          title: "HtmlEditorToolbar.Preview",
          action: () => {
            this.showDialogPreview = true;
            this.codeHTML = this.editor.getHTML();
          }
        },
        {
          icon: "mdi:code-tags",
          title: "HtmlEditorToolbar.sourceCode",
          action: () => {
            this.showDialogCode = true;
            this.codeHTML = this.editor.getHTML();
          },
        },
        {
          icon: "mdi:delete",
          title: "HtmlEditorToolbar.Clear",
          action: () => this.editor.commands.clearContent(),
        },
      ],
    }
  },

  watch: {
    optionText: {
      immediate: true,
      handler(newOption) {
        if (this.editor) {
          newOption === 6 ? this.editor.chain().focus().setParagraph().run() : this.editor.chain().focus().setHeading({ level: newOption }).run();
        }
      }
    },
    openBackground(value) {
      if(value) {
        setTimeout(() => { document.addEventListener('click', this.handleOutsideClick) }, 1000);
      }
    },
    openColor(value) {
      if(value) {
        setTimeout(() => { document.addEventListener('click', this.handleOutsideClick) }, 1000);
      }
    }
  },

  methods: {
    updateColor(title, event) {
      if(title === 'HtmlEditorToolbar.TextColor') {
        this.colorSelected = event;
        this.editor.chain().focus().setColor(event).run();
      } else {
        this.backgroundSelected = event;
        this.editor.chain().focus().toggleHighlight({ color: event }).run();
      }
    },
    setUrl(event) {
      if(this.isLink) {
        if (event.url === "") {
          this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
          return;
        }

        let isImageSelected = this.editor.getAttributes("image");
        let target = event.inputNewWindow ? '_blank' : '_self';

        if(this.isLinkImage) {
          this.editor.chain().focus().setImage({ src: isImageSelected.src, width: isImageSelected.width, height: isImageSelected.height, style: isImageSelected.style }).selectParentNode().setLink({ href: event.url, target: target, text: '' }).run();
          this.editor.commands.createParagraphNear();
        } else {
          this.editor.chain()
            .focus()
            .extendMarkRange('link')
            .setLink({ href: event.url, target: target, text: event.inputText })
            .command(({ tr }) => {
              tr.insertText(event.inputText);

              const { $from } = tr.selection
              tr.insert($from.pos, this.editor.schema.text(' '));

              return tr;
            })
            .run();
        }
      } else {
        let widthImage = event[0].width > 0 ? event[0].width : '';
        let heightImage = event[0].height > 0 ? event[0].height : '';
        let rotationImage = event[0].rotation;
        for (let i = 0; i < event.length; i++) {
          if(i > 0) {
            setTimeout(() => {
              const src = event[i];
              let transform = rotationImage ? rotationImage : 'rotate(0deg)';
              this.editor.chain().focus().setImage({ src: src, width: widthImage, height: heightImage, style: "margin: 0; transform:"+transform+";" }).run();
              this.editor.commands.createParagraphNear();
            }, i * 1000);
          }
        }
      }
    },
    handleOutsideClick(event) {
      this.openColor = false;
      this.openBackground = false;
      document.removeEventListener('click', this.handleOutsideClick);
    },
    updateExternalContent(newContent) {
      this.editor.commands.setContent(newContent, true);
    },

    updateOptionTextFromEditor() {
      if (this.editor) {
        const activeParagraph = this.editor?.isActive("paragraph") ? 1 : 0;
        switch (activeParagraph) {
          case 1:
            this.optionText = 6;
            break;
        }

        const activeHeadingLevel = this.editor.getAttributes('heading').level;
        switch (activeHeadingLevel) {
          case 1:
            this.optionText = 1;
            break;
          case 2:
            this.optionText = 2;
            break;
          case 3:
            this.optionText = 3;
            break;
          case 4:
            this.optionText = 4;
            break;
          case 5:
            this.optionText = 5;
            break;
        }
      }
    },

    insertContent(event) {
      this.editor.commands.insertContent(event, { parseOptions: { preserveWhitespace: false } })
    }
  },

  mounted() {
    this.updateOptionTextFromEditor();
  },

  updated() {
    this.updateOptionTextFromEditor();
  },

  i18n: {
    en: {
      'HtmlEditorToolbar.Heading1': 'Heading 1',
      'HtmlEditorToolbar.Heading2': 'Heading 2',
      'HtmlEditorToolbar.Heading3': 'Heading 3',
      'HtmlEditorToolbar.Heading4': 'Heading 4',
      'HtmlEditorToolbar.Heading5': 'Heading 5',
      'HtmlEditorToolbar.Paragraph': 'Paragraph'
    },
    es: {
      'HtmlEditorToolbar.Heading1': 'Encabezado 1',
      'HtmlEditorToolbar.Heading2': 'Encabezado 2',
      'HtmlEditorToolbar.Heading3': 'Encabezado 3',
      'HtmlEditorToolbar.Heading4': 'Encabezado 4',
      'HtmlEditorToolbar.Heading5': 'Encabezado 5',
      'HtmlEditorToolbar.Paragraph': 'Párrafo'
    },
    de: {
      'HtmlEditorToolbar.Heading1': 'Überschrift 1',
      'HtmlEditorToolbar.Heading2': 'Überschrift 2',
      'HtmlEditorToolbar.Heading3': 'Überschrift 3',
      'HtmlEditorToolbar.Heading4': 'Überschrift 4',
      'HtmlEditorToolbar.Heading5': 'Überschrift 5',
      'HtmlEditorToolbar.Paragraph': 'Absatz'
    },
  },
};
</script>

<style lang="scss">
.cms-html-editor-toolbar {
  position: relative;
  .selectColor {
    z-index: 10;
    position: absolute;
  }
  .UiPicker__current {
    padding: 2px 8px;
  }
}
.divider {
  width: 1px;
  height: 1.25rem;
  margin: 0 0.25rem;
  background-color: rgba(black, 0.25);
}
</style>